import { sxAppend } from '@/libs/material/sx';
import { DS_HEIGHT } from '@calm/evexia-design-tokens';
import commonMessages from '@rhg/lang/messages/common';
import { Button, SxProps, Theme, useTheme } from '@rhg/libs/material';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

const getPadding = (theme: Theme) => ({
    xs: 4 * theme.ds.spacingUnits.SIDE_SMALL,
    sm: 4 * theme.ds.spacingUnits.SIDE_SMALL,
    md: 4 * theme.ds.spacingUnits.SIDE_MEDIUM,
    lg: 4 * theme.ds.spacingUnits.SIDE_LARGE,
});
type Padding = ReturnType<typeof getPadding>;

// Height of the upper actions row.
const UPPER_ACTIONS_HEIGHT = DS_HEIGHT.BUTTON_LARGE;

// Height of the bottom media-controls area, which varies by breakpoint.
const BOTTOM_CONTROLS_HEIGHT = {
    xs: 100,
    sm: 100,
    md: 94,
    lg: 78,
};

const styleAccessibilityButton = {
    zIndex: (theme: Theme) => theme.zIndex.audioSrControls,
    position: 'fixed',
    right: -1000,
    borderRadius: 0,
    top: (theme: Theme) =>
        `${DS_HEIGHT.TOOLBAR + theme.semantics.Spacing[4]}px`,
    ['&:focus']: {
        right: 0,
    },
};

const styleHideControlsButtonUpperArea = (
    padding: Padding,
    actionsCount: number
) => {
    // Actions total width plus their between margin
    const actionsWidth = actionsCount * 48 + (actionsCount - 1) * 16;

    return {
        zIndex: (theme: Theme) => theme.zIndex.mediaControls,
        position: 'absolute',
        top: 0,
        left: {
            xs: 48 + padding.xs,
            sm: 48 + padding.sm,
            md: 48 + padding.md,
            lg: 48 + padding.lg,
        },
        right: {
            xs: actionsWidth + padding.xs,
            sm: actionsWidth + padding.sm,
            md: actionsWidth + padding.md,
            lg: actionsWidth + padding.lg,
        },
        // This calculation accounts for the height of the top details section before the rating CTAs
        height: {
            xs: 7 * 48 + padding.xs,
            sm: 7 * 48 + padding.sm,
            md: 7 * 48 + padding.md,
            lg: 7 * 48 + padding.lg,
        },
        borderRadius: 0,
        cursor: 'default',
        opacity: 0,
    };
};

const styleHideControlsButtonArea = (
    padding: Padding,
    isWholeAreaClickable: boolean,
    hasDialogBottomAction: boolean,
    dialogBottomActionHeight: number = 56
) => ({
    zIndex: (theme: Theme) => theme.zIndex.mediaControls,
    width: '100%',
    position: 'absolute',
    top: isWholeAreaClickable
        ? 0
        : // We want this area to start below the top actions. The actions have
          // height of 48px, and their padding varies based on the screen width.
          {
              xs: 48 + padding.xs,
              sm: 48 + padding.sm,
              md: 48 + padding.md,
              lg: 48 + padding.lg,
          },
    height: isWholeAreaClickable
        ? '100%'
        : // We want the height of this area to equal the height of the page minus
          // the height of the upper actions (48px), minus the bottom controls height (depends on the screen width)
          // minus the page top and bottom padding.
          // the DialogBottomAction factors in the top padding
          {
              xs: `calc(100% - ${
                  2 * padding.xs +
                  UPPER_ACTIONS_HEIGHT +
                  (hasDialogBottomAction ? dialogBottomActionHeight : 0) +
                  BOTTOM_CONTROLS_HEIGHT.xs
              }px)`,
              sm: `calc(100% - ${
                  2 * padding.sm +
                  UPPER_ACTIONS_HEIGHT +
                  (hasDialogBottomAction ? dialogBottomActionHeight : 0) +
                  BOTTOM_CONTROLS_HEIGHT.sm
              }px)`,
              md: `calc(100% - ${
                  2 * padding.md +
                  UPPER_ACTIONS_HEIGHT +
                  (hasDialogBottomAction ? dialogBottomActionHeight : 0) +
                  BOTTOM_CONTROLS_HEIGHT.md
              }px)`,
              lg: `calc(100% - ${
                  2 * padding.lg +
                  UPPER_ACTIONS_HEIGHT +
                  (hasDialogBottomAction ? dialogBottomActionHeight : 0) +
                  BOTTOM_CONTROLS_HEIGHT.lg
              }px)`,
          },
    borderRadius: 0,
    cursor: 'default',
    opacity: 0,
});

interface ToggleControlsAreaProps {
    actionsCount: number;
    areControlsHidden: boolean;
    hasDialogBottomAction?: boolean;
    dialogBottomActionHeight?: number;
    onToggle: () => void;
    sx?: SxProps<Theme>;
}

/**
 * We want this area to cover the whole page, except for the area covered by the
 * upper actions and bottom controls.
 */
export function ToggleControlsArea({
    actionsCount,
    areControlsHidden,
    onToggle,
    hasDialogBottomAction = false,
    dialogBottomActionHeight = 56,
    sx,
}: ToggleControlsAreaProps) {
    const { formatMessage } = useIntl();
    const theme = useTheme();

    const controlsToggleAreaStyle = useMemo(
        () =>
            styleHideControlsButtonArea(
                getPadding(theme),
                areControlsHidden,
                hasDialogBottomAction,
                dialogBottomActionHeight
            ),
        [
            theme,
            areControlsHidden,
            hasDialogBottomAction,
            dialogBottomActionHeight,
        ]
    );

    const controlsToggleUpperAreaStyle = useMemo(
        () => styleHideControlsButtonUpperArea(getPadding(theme), actionsCount),
        [theme, actionsCount]
    );

    const label = useMemo(
        () =>
            areControlsHidden
                ? formatMessage(commonMessages['common.show_controls'])
                : formatMessage(commonMessages['common.hide_controls']),
        [areControlsHidden, formatMessage]
    );

    return (
        <>
            {/* This button covers the area between the upper actions. We want this to
                render only when the controls are displayed, because when the controls
                are hidden, the button below covers the whole page.
            */}
            {!areControlsHidden && (
                <Button
                    aria-hidden
                    tabIndex={-1}
                    skipTracking
                    onClick={onToggle}
                    sx={controlsToggleUpperAreaStyle}
                />
            )}

            <Button
                aria-hidden
                tabIndex={-1}
                skipTracking
                onClick={onToggle}
                sx={sxAppend(controlsToggleAreaStyle, sx)}
            />

            {/* For screen readers */}
            <Button
                sx={styleAccessibilityButton}
                skipTracking
                variant="contained"
                color="secondary"
                onClick={onToggle}
            >
                {label}
            </Button>
        </>
    );
}
